import React from "react";
import {
  Box,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DoneAll, DoneOutlined, ErrorOutlined } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { formatDateWithTime, formatText } from "../../../helpers";
import { IWhatsAppActivity } from "../../../redux/activity/activity.types";
import { PreviewTemplate } from "../../../components/Preview/PreviewTemplate";
import { ReplyPreviewTemplate } from "../../../components/Preview/ReplyPreviewTemplate";
import { MessageActionsPopover } from "../components/MessageActionsPopover";
import { IWhatsAppTemplateData } from "../../../redux/whatsappTemplate/whatsappTemplate.types";

interface IWhatsAppMessageRowProps {
  data: IWhatsAppActivity;
  currentPhone: string;
  whatsappNumber: string;
  scannedPhoneNumber: string;
  propUseQRSend: boolean;
  messagesList: IWhatsAppActivity[];
  onReplySelect: (message: IWhatsAppActivity) => void;
  onForwardMessage?: (data: IWhatsAppActivity) => void;
  onRestrictMessage?: (data: IWhatsAppActivity) => void;
  onShowRestrictedMessage?: (data: IWhatsAppActivity) => void;
  visibleRestrictedMessages?: Set<string>;
}

export const WhatsAppMessageRow: React.FC<IWhatsAppMessageRowProps> = ({
  data,
  currentPhone,
  whatsappNumber,
  scannedPhoneNumber,
  propUseQRSend,
  messagesList,
  onReplySelect,
  onForwardMessage,
  onRestrictMessage,
  onShowRestrictedMessage,
  visibleRestrictedMessages = new Set(),
}) => {
  const [openView, setOpenView] = React.useState(false);
  const theme = useTheme();
  const [isHovered, setIsHovered] = React.useState(false);
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
  const handleToggle = () => setOpenView(!openView);

  // ✅ Get replied message if reply_from_conversation_id exists
  const repliedMessage = React.useMemo(() => {
    if (!data?.reply_from_conversation_id || !messagesList?.length) return null;
    return messagesList.find(
      (msg) => msg.conversation_id === data.reply_from_conversation_id
    );
  }, [data?.reply_from_conversation_id, messagesList]);

  const renderStatus = React.useMemo(() => {
    switch (data?.status) {
      case "INITIATE":
        return (
          <Tooltip title="Sent" arrow>
            <DoneOutlined fontSize="small" color="disabled" />
          </Tooltip>
        );
      case "DELIVERED":
        return (
          <Tooltip title="Delivered" arrow>
            <DoneAll fontSize="small" color="disabled" />
          </Tooltip>
        );
      case "READ":
        return (
          <Tooltip title="Seen" arrow>
            <DoneAll fontSize="small" color="secondary" />
          </Tooltip>
        );
      case "SERVICE_OFF":
        return (
          <Tooltip title="Service Off" arrow>
            <ErrorOutlined color="error" fontSize="small" />
          </Tooltip>
        );
      default:
        return (
          <Tooltip title={formatText(data?.status || "")} arrow>
            <ErrorOutlined color="error" fontSize="small" />
          </Tooltip>
        );
    }
  }, [data?.status]);

  const isSender = scannedPhoneNumber
    ? scannedPhoneNumber !== data?.to_number
    : whatsappNumber !== data?.to_number;

  // Helper function to mask restricted messages
  const maskRestrictedMessage = (text: string): string => {
    // Show first character, next 4 as "*", then "..."
    return `${text[0]}${"*".repeat(text.length > 5 ? 10 : text.length)}${text.length > 5 ? "..." : ""
      }`;
  };

  // Helper function to check if message is restricted
  const isMessageRestricted = (
    data: IWhatsAppActivity,
    contentItem?: any
  ): boolean => {
    // Check message-level is_restricted (can be 1, true, or any truthy value)
    const messageLevelRestricted =
      data?.is_restricted === 1 ||
      data?.is_restricted === true ||
      Boolean(data?.is_restricted);

    // Check content-item-level is_restricted
    const contentLevelRestricted =
      contentItem?.is_restricted === 1 ||
      contentItem?.is_restricted === true ||
      Boolean(contentItem?.is_restricted);

    return messageLevelRestricted || contentLevelRestricted;
  };

  const getTemplateData = (data: IWhatsAppActivity): IWhatsAppTemplateData => {
    if (!data || !data.content) {
      return {
        name: "",
        category: "",
        example: { header_text: [], body_text: [] },
        components: [],
        header: {
          type: "HEADER",
          format: "TEXT",
          text: null,
          example: { header_text: [], header_handle: null },
        },
        body: { type: "BODY", text: "", example: { body_text: [] } },
        buttons: { type: "BUTTONS", buttons: [] },
        footer: { type: "FOOTER", text: "" },
        status: null,
        rejected_reason: null,
        company_outlet_uuid: "",
        attachments: [],
        content: [],
      };
    }

    const baseTemplate: IWhatsAppTemplateData = {
      name: "",
      category: "",
      example: { header_text: [], body_text: [] },
      components: [],
      header: {
        type: "HEADER",
        format: "TEXT",
        text: null,
        example: { header_text: [], header_handle: null },
      },
      body: {
        type: "BODY",
        text: (() => {
          const firstContent = data?.content?.[0];
          const isRestricted = isMessageRestricted(data, firstContent);
          const text = firstContent?.text || "";
          const isVisible = visibleRestrictedMessages.has(data.conversation_id);

          if (isRestricted && text) {
            return isVisible ? text : maskRestrictedMessage(text);
          }
          return text;
        })(),
        example: { body_text: [] },
      },
      buttons: { type: "BUTTONS", buttons: [] },
      footer: { type: "FOOTER", text: "" },
      status: null,
      rejected_reason: null,
      company_outlet_uuid: "",
      attachments: data.attachments || [],
      content: Array.isArray(data.content)
        ? data.content.map((item) =>
          typeof item === "string" ? item : JSON.stringify(item)
        )
        : [],
    };

    data.content?.forEach((contentItem) => {
      if (!contentItem) return;
      switch (contentItem.type) {
        case "HEADER":
          baseTemplate.header = {
            type: "HEADER",
            format: contentItem.format,
            text: contentItem.text || null,
            example: {
              header_text: [],
              header_handle:
                contentItem.example?.header_handle?.map((handle, index) => ({
                  key: `file-${index}`,
                  file: null,
                  path: handle,
                  url: handle,
                })) || null,
            },
          };
          break;
        case "BODY":
          // Check both message-level and content-item-level is_restricted
          const isRestricted = isMessageRestricted(data, contentItem);
          const isVisible = visibleRestrictedMessages.has(data.conversation_id);
          baseTemplate.body = {
            type: "BODY",
            text:
              isRestricted && contentItem.text
                ? isVisible
                  ? contentItem.text
                  : maskRestrictedMessage(contentItem.text)
                : contentItem.text || "",
            example: { body_text: [] },
          };
          break;
        case "FOOTER":
          baseTemplate.footer = {
            type: "FOOTER",
            text: contentItem.text,
          };
          break;
        case "BUTTONS":
          baseTemplate.buttons = {
            type: "BUTTONS",
            buttons:
              contentItem.buttons?.map((btn) => ({
                type: btn.type,
                text: btn.text,
              })) || [],
          };
          break;
      }
    });

    return baseTemplate;
  };

  const templateData = getTemplateData(data);

  // ✅ Scroll to replied message
  const handleReplyClick = React.useCallback(() => {
    if (!repliedMessage?.conversation_id) return;
    const targetEl = document.getElementById(
      `msg-${repliedMessage.conversation_id}`
    );
    if (targetEl) {
      targetEl.scrollIntoView({ behavior: "smooth", block: "center" });
      // Optional: add a short highlight flash effect
      targetEl.classList.add("flash-highlight");
      setTimeout(() => {
        targetEl.classList.remove("flash-highlight");
      }, 1500);
    }
  }, [repliedMessage]);

  return (
    <Box
      id={`msg-${data.conversation_id}`} // ✅ unique id for scroll target
      position="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onDoubleClick={(event) => {
        event.stopPropagation();
        onReplySelect(data);
      }}
      onClick={(event) => {
        if (event.detail === 2) return;
        handleToggle();
      }}
      sx={{
        display: "flex",
        justifyContent: isSender ? "flex-end" : "flex-start",
        // width: isDesktop ? "100%" : "80%",
        mb: 2,
        scrollMarginTop: "80px", // in case of sticky header
      }}
    >
      <Stack
        sx={{
          width: "fit-content",
          maxWidth: { xs: "80%", sm: "70%" },
          // pl: isSender ? 0 : 2,
          // pr: isSender ? 2 : 0,
          // [theme.breakpoints.down("sm")]: {
          //   pl: isSender ? 0 : 1,
          //   pr: isSender ? 1 : 0,
          // },
        }}
      >
        {/* ✅ Reply Preview UI */}
        {repliedMessage && (
          <>
            <Box position="relative">
              {/* 3-dots menu for message actions */}
              <Box
                sx={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  zIndex: 1000,
                  opacity: isHovered ? 1 : 0,
                  transition: "opacity 0.2s ease",
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: "50%",
                  boxShadow: theme.shadows[2],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <MessageActionsPopover
                  message={data}
                  onReply={onReplySelect}
                  onForward={onForwardMessage}
                  onRestrict={onRestrictMessage}
                  onShowRestrictedMessage={onShowRestrictedMessage}
                  isRestrictedMessageVisible={visibleRestrictedMessages.has(
                    data.conversation_id
                  )}
                  isSender={isSender}
                />
              </Box>
              <Paper
                elevation={0}
                onClick={(e) => {
                  e.stopPropagation(); // prevent toggling view
                  handleReplyClick();
                }}
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.9)}`,
                  bgcolor: theme.palette.primary.main,
                  cursor: "pointer",
                }}
              >
                <Stack
                  sx={{
                    p: 1.5,
                    gap: 1,
                  }}
                >
                  <Stack
                    direction={"column"}
                    spacing={0.75}
                    sx={{
                      p: 1.25,
                      borderLeft: 3,
                      borderRadius: 3,
                      borderColor: "#d9d9d9",
                      bgcolor: "#092d4d",
                      color: "#d9d9d9",
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        maxHeight: 150,
                        overflow: "hidden",
                        wordBreak: "break-word",
                      }}
                    >
                      <ReplyPreviewTemplate
                        template={getTemplateData(repliedMessage) as any}
                        headerType=""
                        headerVariableValue=""
                        bgImage={false}
                        createdByName={
                          repliedMessage?.created_by_name || "Replied message"
                        }
                      />
                    </Box>
                  </Stack>

                  <Box>
                    <PreviewTemplate
                      template={templateData as any}
                      headerType=""
                      headerVariableValue=""
                      bgImage={false}
                      renderInsideReply
                    />
                  </Box>
                </Stack>
              </Paper>
            </Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" ml={1} textAlign="start" mt={0.5}>
                {`(By - ${data?.created_by_name || ""}) ${formatDateWithTime(
                  data?.insert_ts
                )}`}
              </Typography>
              {isSender && <Stack>{renderStatus}</Stack>}
            </Stack>
          </>
        )}

        {/* ✅ Actual message */}
        {!repliedMessage && (
          <>
            <Box position="relative">
              {/* 3-dots menu for message actions */}
              <Box
                sx={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  zIndex: 1000,
                  opacity: isHovered ? 1 : 0,
                  transition: "opacity 0.2s ease",
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: "50%",
                  boxShadow: theme.shadows[2],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <MessageActionsPopover
                  message={data}
                  onReply={onReplySelect}
                  onForward={onForwardMessage}
                  onRestrict={onRestrictMessage}
                  onShowRestrictedMessage={onShowRestrictedMessage}
                  isRestrictedMessageVisible={visibleRestrictedMessages.has(
                    data.conversation_id
                  )}
                  isSender={isSender}
                />
              </Box>
              <PreviewTemplate
                template={templateData as any}
                headerType=""
                headerVariableValue=""
                bgImage={false}
              />
            </Box>

            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" ml={1} textAlign="start" mt={0.5}>
                {`(By - ${data?.created_by_name || ""}) ${formatDateWithTime(
                  data?.insert_ts
                )}`}
              </Typography>
              {isSender && <Stack>{renderStatus}</Stack>}
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
};

export default WhatsAppMessageRow;
